import { Alert, Badge, Button, Flex, Group, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TravelApprovalActions } from '@/module/Travel/Approval/components/TravelApprovalActions';
import { TravelApprovalTimeline } from '@/module/Travel/Approval/components/TravelApprovalTimeline';

import { Content, EditButtonLink } from '@/core/components';

import { useCurrentUser } from '@feature/security';
import { formatDate, notEmptyArray, useScrollToHashOnceSettled } from '@feature/utils';

import { TravelOverlapStrip } from './components/TravelOverlapStrip';
import { isTravelOverlapRole } from './components/TravelOverlapUtils';
import { TravelProcessStepKey, TravelProcessTimeline } from './components/TravelProcessTimeline';
import { TravelRegistryTypeBadge } from './components/TravelRegistryTypeBadge';
import { TravelRequestCancelButton } from './components/TravelRequestCancelButton';
import { TravelRequestCancellationAlert } from './components/TravelRequestCancellationAlert';
import { TravelRequestCompleteButton } from './components/TravelRequestCompleteButton';
import { TravelRequestDayBalance } from './components/TravelRequestDayBalance';
import { TravelRequestDeleteButton } from './components/TravelRequestDeleteButton';
import { TravelRequestDocumentButton } from './components/TravelRequestDocumentButton';
import { TravelRequestNextAlert } from './components/TravelRequestNextAlert';
import { TravelRequestReturnDateNotice } from './components/TravelRequestReturnDateNotice';
import { TravelRequestStatusBadge } from './components/TravelRequestStatusBadge';
import { TravelRequestSubmitStep } from './components/TravelRequestSubmitStep';
import { TravelRequestTravelOrderButton } from './components/TravelRequestTravelOrderButton';
import { TravelRequestBoardDecisionFiles } from './components/files/TravelRequestBoardDecisionFiles';
import { TravelRequestFiles } from './components/files/TravelRequestFiles';
import { getTravelDocumentKindsOptions } from './model/TravelRequestQuery';
import {
  TravelRequest,
  absenceMotivationLabel,
  formatTravelRegistration,
  hasTravelMobilitySupport,
  sortTravelApprovalSteps,
  travelBoardReached,
  travelDestinationInstitutionField,
  travelRequestPermissions,
  travelReturnDocumentsComplete,
  travelSubmissionDocumentsComplete,
} from './model/TravelRequestTypes';

interface TravelRequestViewProps {
  request: TravelRequest;
}

interface FieldProps {
  label: string;
  value: ReactNode;
}

function withEurEquivalent(amount: string, eurAmount: number | null) {
  return eurAmount == null ? amount : `${amount} (${eurAmount} EUR)`;
}

function Field({ label, value }: FieldProps) {
  if (value == null || value === '') {
    return null;
  }

  return (
    <Group gap="xs">
      <Text
        size="sm"
        c="dimmed"
      >
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </Group>
  );
}

export function TravelRequestView({ request }: TravelRequestViewProps) {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const isRequester = request.requester.id === currentUser.id;
  const { canEdit, canSubmit, canDelete } = travelRequestPermissions(
    request.status,
    isRequester,
    currentUser.currentRole,
  );
  const isApproved = request.status === 'APPROVED';
  const {
    data: documentKinds,
    isError: documentKindsFailed,
    refetch: refetchDocumentKinds,
  } = useQuery({
    ...getTravelDocumentKindsOptions(request.activityType, hasTravelMobilitySupport(request)),
    enabled: canSubmit || isApproved || request.status === 'DRAFT',
  });
  const returnDocumentsComplete =
    isApproved && documentKinds != null && travelReturnDocumentsComplete(request, documentKinds);
  const submissionDocumentsComplete = travelSubmissionDocumentsComplete(request, documentKinds);
  useScrollToHashOnceSettled();

  const {
    activityType,
    erasmusType,
    name,
    destinationInstitution,
    city,
    country,
    departureDate,
    returnDate,
    eventStartDate,
    eventEndDate,
    requestDate,
    presentsPaper,
    paperTitle,
    lateSubmission,
    affiliation,
    registration,
    absence,
    funding,
    expenses,
    approvalSteps,
  } = request;

  const activityTypeLabel = t(`travel.activityTypeEnum.${activityType}`);
  const isErasmus = activityType === 'ERASMUS';
  const place = [city, country?.name].filter(notEmptyArray).join(', ');
  const affiliationParts = [
    affiliation?.facultyName,
    affiliation?.departmentName,
    affiliation?.disciplineName,
    affiliation?.jobTitle,
  ].filter(notEmptyArray);
  const registrationValue = registration
    ? formatTravelRegistration(registration)
    : t('travel.requests.view.notRegistered');
  const showProjectColumns = expenses.some(
    expense => expense.projectNumber != null || expense.projectTitle != null || expense.projectAmount != null,
  );
  const latestReturnedStep = sortTravelApprovalSteps(approvalSteps)
    .reverse()
    .find(step => step.decision === 'RETURNED');
  const returnReason = request.status === 'RETURNED_FOR_CHANGES' ? (latestReturnedStep?.reason ?? null) : null;
  const showReturnFiles =
    request.status === 'APPROVED' || request.status === 'COMPLETED' || request.status === 'CANCELLED';
  const boardSteps = approvalSteps.filter(step => step.stepKey === 'ADMINISTRATIVE_BOARD');
  const academicSteps = approvalSteps.filter(step => step.stepKey !== 'ADMINISTRATIVE_BOARD');
  const showBoardStep = !isErasmus;
  // The strip fetches on mount, so a role the endpoint refuses must not render it.
  const showOverlaps = isTravelOverlapRole(currentUser.currentRole);

  const stepContent: Partial<Record<TravelProcessStepKey, ReactNode>> = {
    fill: (
      <Stack gap="sm">
        {returnReason && (
          <Alert
            color="yellow"
            title={t('travel.requests.view.returnReason')}
          >
            {returnReason}
          </Alert>
        )}
        {canEdit && (
          <Group>
            <EditButtonLink
              size="xs"
              miw="100px"
            />
          </Group>
        )}

        <Content
          type="full"
          title={t('travel.requests.form.sections.general')}
        >
          <Field
            label={t('travel.requests.form.fields.activityType')}
            value={activityTypeLabel}
          />
          {erasmusType && (
            <Field
              label={t('travel.requests.form.fields.erasmusType')}
              value={t(`travel.erasmusTypeEnum.${erasmusType}`)}
            />
          )}
          <Field
            label={t(`travel.requests.form.fields.${travelDestinationInstitutionField(activityType)}`)}
            value={destinationInstitution}
          />
          <Field
            label={t('travel.requests.form.fields.city')}
            value={place}
          />
          <Field
            label={t('travel.requests.form.fields.departureDate')}
            value={formatDate(departureDate)}
          />
          <Field
            label={t('travel.requests.form.fields.returnDate')}
            value={formatDate(returnDate)}
          />
          {eventStartDate != null && (
            <Field
              label={t('travel.requests.form.fields.eventStartDate')}
              value={formatDate(eventStartDate)}
            />
          )}
          {eventEndDate != null && (
            <Field
              label={t('travel.requests.form.fields.eventEndDate')}
              value={formatDate(eventEndDate)}
            />
          )}
          {presentsPaper != null && (
            <Field
              label={t('travel.requests.form.fields.presentsPaper')}
              value={t(presentsPaper ? 'bool.YES' : 'bool.NO')}
            />
          )}
          <Field
            label={t('travel.requests.form.fields.paperTitle')}
            value={paperTitle}
          />
        </Content>

        {absence && (
          <Content
            type="full"
            title={t('travel.requests.form.sections.absence')}
          >
            {absence.teachingCoverageMode && (
              <Field
                label={t('travel.requests.form.fields.teachingCoverageMode')}
                value={t(`travel.teachingCoverageModeEnum.${absence.teachingCoverageMode}`)}
              />
            )}
            <Field
              label={t('travel.requests.form.fields.teachingCoverageNote')}
              value={absence.teachingCoverageNote}
            />
            <Field
              label={t('travel.requests.form.fields.substitutePersonName')}
              value={absence.substitutePersonName}
            />
            {absence.absenceMotivation && (
              <Field
                label={t('travel.requests.form.fields.absenceMotivation')}
                value={absenceMotivationLabel(absence.absenceMotivation, t)}
              />
            )}
            <Field
              label={t('travel.requests.form.fields.vacationDays')}
              value={absence.vacationDays}
            />
            <Field
              label={t('travel.requests.form.fields.unpaidDays')}
              value={absence.unpaidDays}
            />
            <Field
              label={t('travel.requests.form.fields.ownProgramsMention')}
              value={absence.ownProgramsMention}
            />
          </Content>
        )}

        {funding && (!isErasmus || funding.estimatedAmount != null) && (
          <Content
            type="full"
            title={t('travel.requests.form.sections.funding')}
          >
            {!isErasmus && (
              <>
                <Field
                  label={t('travel.requests.form.fields.perDiem')}
                  value={t(funding.perDiem ? 'bool.YES' : 'bool.NO')}
                />
                <Field
                  label={t('travel.requests.form.fields.fundingSourceNote')}
                  value={funding.fundingSourceNote}
                />
              </>
            )}
            {isErasmus && (
              <Field
                label={t('travel.requests.form.fields.estimatedAmount')}
                value={
                  funding.estimatedAmount == null
                    ? null
                    : [funding.estimatedAmount, funding.estimatedAmountCurrency].filter(notEmptyArray).join(' ')
                }
              />
            )}
          </Content>
        )}

        <Content
          type="full"
          title={t('travel.requests.view.expenses')}
        >
          {expenses.length === 0 ? (
            <Text
              size="sm"
              c="dimmed"
              fs="italic"
            >
              {t('travel.requests.view.noExpenses')}
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('travel.requests.form.fields.expenseType')}</Table.Th>
                  <Table.Th>{t('travel.requests.form.fields.fundingSource')}</Table.Th>
                  <Table.Th>{t('travel.requests.form.fields.requestedAmount')}</Table.Th>
                  {showProjectColumns && <Table.Th>{t('travel.requests.form.fields.projectNumber')}</Table.Th>}
                  {showProjectColumns && <Table.Th>{t('travel.requests.form.fields.projectTitle')}</Table.Th>}
                  {showProjectColumns && <Table.Th>{t('travel.requests.form.fields.projectAmount')}</Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {expenses.map(expense => (
                  <Table.Tr key={expense.id}>
                    <Table.Td>{t(`travel.expenseTypeEnum.${expense.expenseType}`)}</Table.Td>
                    <Table.Td>{t(`travel.fundingSourceEnum.${expense.fundingSource}`)}</Table.Td>
                    <Table.Td>
                      {expense.requestedAmount == null
                        ? '-'
                        : withEurEquivalent(`${expense.requestedAmount} ${expense.currency}`, expense.eurAmount)}
                    </Table.Td>
                    {showProjectColumns && <Table.Td>{expense.projectNumber ?? '-'}</Table.Td>}
                    {showProjectColumns && <Table.Td>{expense.projectTitle ?? '-'}</Table.Td>}
                    {showProjectColumns && (
                      <Table.Td>
                        {expense.projectAmount == null
                          ? '-'
                          : withEurEquivalent(`${expense.projectAmount} ${expense.currency}`, expense.projectEurAmount)}
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Content>
      </Stack>
    ),
    attach: (
      <TravelRequestFiles
        request={request}
        stage="submission"
      />
    ),
    submit: canSubmit ? (
      <TravelRequestSubmitStep
        request={request}
        documentKinds={documentKinds}
      />
    ) : null,
    approval: (
      <Stack gap="sm">
        {academicSteps.length > 0 && <TravelApprovalTimeline steps={academicSteps} />}
        {/* Zero is a real answer for the count, so it always shows; the warning only fires when there is something to flag. */}
        <Alert color="blue">{t('travel.requests.view.parallelBanner', { count: request.parallelRequests })}</Alert>
        {request.unfinishedReturns > 0 && (
          <Alert color="red">
            {t('travel.requests.view.unfinishedReturnsWarning', { count: request.unfinishedReturns })}
          </Alert>
        )}
        {/* Each step keeps its own instance mounted, since unmounting one mid-decision drops its success toast. */}
        <Group>
          <TravelApprovalActions
            request={request}
            board={false}
          />
        </Group>
      </Stack>
    ),
    administrativeBoard:
      approvalSteps.length > 0 ? (
        <Stack gap="sm">
          {boardSteps.length > 0 && <TravelApprovalTimeline steps={boardSteps} />}
          <TravelRequestBoardDecisionFiles request={request} />
          <Group>
            <TravelApprovalActions
              request={request}
              board
            />
          </Group>
        </Stack>
      ) : null,
    travelOrder: request.travelOrder.available ? (
      <Group>
        <TravelRequestTravelOrderButton request={request} />
      </Group>
    ) : null,
    returnDocuments: showReturnFiles ? (
      <TravelRequestFiles
        request={request}
        stage="return"
      />
    ) : null,
    complete: request.canComplete ? (
      <Group>
        <TravelRequestCompleteButton request={request} />
      </Group>
    ) : (
      <TravelRequestReturnDateNotice request={request} />
    ),
  };

  return (
    <Stack>
      <TravelRequestCancellationAlert request={request} />
      <TravelRequestNextAlert
        request={request}
        isRequester={isRequester}
        submissionDocumentsComplete={submissionDocumentsComplete}
        returnDocumentsComplete={returnDocumentsComplete}
      />
      {request.status === 'DRAFT' && documentKindsFailed && (
        <Alert color="red">
          <Group
            justify="space-between"
            gap="xs"
          >
            <Text size="sm">{t('travel.requests.view.documentKindsError')}</Text>
            <Button
              size="xs"
              variant="light"
              onClick={() => refetchDocumentKinds()}
            >
              {t('travel.requests.view.documentKindsRetry')}
            </Button>
          </Group>
        </Alert>
      )}
      {showOverlaps && (
        <TravelOverlapStrip
          key={request.id}
          request={request}
        />
      )}
      <Content type="full">
        <Stack gap="10px">
          <Flex
            justify="space-between"
            wrap="wrap"
            gap="xs"
          >
            <Stack
              gap="6px"
              align="flex-start"
            >
              <Group gap="xs">
                <Text fw="500">{name ?? activityTypeLabel}</Text>
                <TravelRequestStatusBadge status={request.status} />
                {lateSubmission && <Badge color="red">{t('travel.requests.view.lateSubmission')}</Badge>}
              </Group>
              {registration && <TravelRegistryTypeBadge registryType={registration.registryType} />}
            </Stack>
            <Group>
              <TravelRequestDocumentButton request={request} />
              <TravelRequestTravelOrderButton request={request} />
              <TravelRequestCancelButton request={request} />
              {canDelete && <TravelRequestDeleteButton requestId={request.id} />}
            </Group>
          </Flex>

          <Group gap="xs">
            <Text
              size="sm"
              c="dimmed"
            >
              {t('travel.requests.view.registration')}
            </Text>
            <Badge
              size="lg"
              variant="light"
              color={registration ? 'blue' : 'gray'}
              tt="none"
            >
              {registrationValue}
            </Badge>
          </Group>
          <Field
            label={t('travel.requests.view.requestDate')}
            value={formatDate(requestDate)}
          />
          <Field
            label={t('travel.requests.view.requester')}
            value={request.requester.name}
          />
          <Field
            label={t('travel.requests.view.affiliation')}
            value={affiliationParts.join(', ')}
          />
          <TravelRequestDayBalance
            activityType={request.activityType}
            leaveDaysExceeded={request.leaveDaysExceeded}
            dayCapExceeded={request.dayCapExceeded}
            dayBalance={request.dayBalance}
          />
        </Stack>
      </Content>

      <TravelProcessTimeline
        status={request.status}
        submissionDocumentsComplete={submissionDocumentsComplete}
        returnDocumentsComplete={returnDocumentsComplete}
        showBoardStep={showBoardStep}
        boardReached={travelBoardReached(request)}
        content={stepContent}
      />
    </Stack>
  );
}
